import React from "react";
import {
  useTheme,
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Stack,
  Alert,
} from "@mui/material";
import {
  CloudDownload,
  TableChart,
  Search,
  Description,
  CheckCircle,
  Public,
  Assessment,
  PictureAsPdf,
  Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import danniMF from "../assets/excel/Danni_MF.xls";
import danniNSI from "../assets/excel/Danni_NSI_2022.xlsx";
import danniNAP from "../assets/excel/Danni_NAP.xlsx";
import danniMFPdf from "../assets/pdf/Danni_MF.pdf";
import danniNSIPdf from "../assets/pdf/Danni_NSI_2022.pdf";
import danniNAPPdf from "../assets/pdf/Danni_NAP.pdf";
import databaseXlsx from "../assets/excel/Database.xlsx";
import dataContents from "../assets/pdf/Data_contends.pdf";

import excelIconPng from "../assets/icons/xls-file.png";

const DatabasePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const excelIcon = (
    <Box
      component="img"
      src={excelIconPng}
      alt="Excel"
      sx={{ width: 20, height: 20 }}
    />
  );

  const baseFiles = [
    {
      id: 1,
      title: "Пълна интегрирана база данни (всички нива и индикатори)",
      excelFile: danniMF,
      excelName: "Danni_MF.xls",
      pdfFile: danniMFPdf,
      pdfName: "Danni_MF.pdf",
      description: "",
      viewPath: "",
    },
  ];

  const downloadFiles = [
    {
      id: 1,
      title: "Финансиране и субсидирани бройки (МФ)",
      excelFile: danniMF,
      excelName: "Danni_MF.xls",
      pdfFile: danniMFPdf,
      pdfName: "Danni_MF.pdf",
      description: "Таблица 1:",
      viewPath: "/database/mf",
    },
    {
      id: 2,
      title: "Кадри и образователна структура (НСИ)",
      excelFile: danniNSI,
      excelName: "Danni_NSI_2022.xlsx",
      pdfFile: danniNSIPdf,
      pdfName: "Danni_NSI_2022.pdf",
      description: "Таблица 2:",
      viewPath: "/database/nsi",
    },
    {
      id: 3,
      title: "Осигурителен доход и заетост по общини (НАП)",
      excelFile: danniNAP,
      excelName: "Danni_NAP.xlsx",
      pdfFile: danniNAPPdf,
      pdfName: "Danni_NAP.pdf",
      description: "Таблица 3:",
      viewPath: "/database/nap",
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4, maxWidth: 1400 }}>
      <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Интегрирана база данни на читалищната мрежа
        </Typography>

        <Typography paragraph sx={{ mb: 3 }}>
          Освен обобщените данни на ниво община, тук може да намерите първата в
          България интегрирана база данни, която свързва дейността на читалищата
          със социално-икономическия профил на населените места и общините.
          Масивът включва над 100 индикатора за периода 2021–2023 г. и е
          организиран на три нива:
        </Typography>

        <Box sx={{ mb: 4 }}>
          <List>
            <ListItem sx={{ alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <CheckCircle color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Индивидуално"
                secondary="Данни за всяко конкретно читалище."
              />
            </ListItem>

            <ListItem sx={{ alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <Public color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Населено място"
                secondary="Контекст на средата, в която функционира организацията."
              />
            </ListItem>

            <ListItem sx={{ alignItems: "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <Assessment color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Община"
                secondary="Макрорамка с демографски, икономически и социални показатели."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Източници и методология
          </Typography>

          <Typography paragraph sx={{ mb: 2 }}>
            Данните са събрани чрез заявления по ЗДОИ, автоматизирано извличане
            (Data Scraping) от електронни регистри и експертен анализ.
            Интегрирана е информация от следните публични регистри и институции:
          </Typography>

          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Официална статистика:
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              НСИ, Министерство на финансите, НАП и МРРБ.
            </Typography>

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Регистри към Министерство на културата:
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              Регистър на читалищата и читалищните сдружения и Регистър на
              обществените библиотеки.
            </Typography>

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Агенция по вписванията:
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              Търговски регистър и регистър на ЮЛНЦ (годишни финансови отчети).
            </Typography>

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Икономически профили:
            </Typography>
            <Typography paragraph>
              База данни на Института за пазарна икономика.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: "rgba(255, 243, 205, 0.3)",
            borderLeft: "4px solid #ffc107",
            borderRadius: "4px",
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Приоритетност и ограничения
          </Typography>

          <Typography paragraph sx={{ mb: 1 }}>
            С цел максимална достоверност, с приоритет са ползвани данните от
            НСИ, МФ и НАП. Информацията от регистрите към Министерството на
            културата и ТР/ЮЛНЦ е използвана селективно поради установени
            несъответствия и административни ограничения.
          </Typography>

          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Важно: Базата данни е актуална към декември 2025 г. Информация за
            всяко от 3597-те читалища поотделно, включително детайли за неговата
            културна и библиотечна дейност, можете да намерите в Първата онлайн
            карта на читалищата в България.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Файлове за изтегляне
          </Typography>

          <Typography paragraph sx={{ mb: 3 }}>
            Тук можете да изтеглите суровите данни в удобен за обработка формат
            (.xlsx) или като PDF документ:
          </Typography>

          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "80%", md: "60%" },
                p: 3,
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  mb: 2,
                  textAlign: "center",
                  color: "text.primary",
                }}
              >
                Пълна интегрирана база данни (всички нива и индикатори):
              </Typography>
              <Stack spacing={2} sx={{ mb: 4 }}>
                <Button
                  startIcon={excelIcon}
                  variant="contained"
                  fullWidth
                  href={databaseXlsx}
                  download={"Database.xlsx"}
                  sx={{
                    mb: 2,
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    py: 1.5,
                    border: 1,
                    borderColor: "#1976d2",
                    color: "white",
                    justifyContent: "center",
                    backgroundColor: "#1976d2",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1565c0",
                      color: "#ffffff",
                      borderColor: "#1565c0",
                      boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Изтегли Excel формат
                </Button>

                <Button
                  startIcon={<TableChart />}
                  variant="contained"
                  fullWidth
                  href={"https://bit.ly/chitalishta-master-table "}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: "1.125rem", // По-голям шрифт
                    fontWeight: 600,
                    py: 1.5, // По-голям padding
                    border: 1,
                    borderColor: "#d32f2f",
                    color: "#d32f2f",
                    justifyContent: "center",
                    backgroundColor: "transparent",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                    "&:hover": {
                      borderColor: "#c62828",
                      backgroundColor: "rgba(211, 47, 47, 0.04)",
                      boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                      // transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Линк към Google Sheet
                </Button>
              </Stack>

              <Stack spacing={1}>
                <Alert severity="warning" sx={{ my: 2 }}>
                  <strong>Важно:</strong> Изтеглете база данни и легенда за пълна
                  информация!
                </Alert>

                <Button
                  startIcon={<Description />}
                  variant="outlined"
                  fullWidth
                  href={"https://bit.ly/chitalishta-master-table-legend"}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    justifyContent: "flex-start",
                    pl: 2,
                    border: "none",
                    // borderColor: "#1976d2",
                    color: "#1976d2",
                    "&:hover": {
                      // borderColor: "#1565c0",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  Линк към легенда и обяснения
                </Button>

                <Button
                  startIcon={<PictureAsPdf />}
                  variant="outlined"
                  fullWidth
                  href={dataContents}
                  download={"Data_contents.pdf"}
                  sx={{
                    justifyContent: "flex-start",
                    pl: 2,
                    // borderColor: "#d32f2f",
                    border: "none",
                    color: "#d32f2f",
                    "&:hover": {
                      borderColor: "#c62828",
                      backgroundColor: "rgba(211, 47, 47, 0.04)",
                    },
                  }}
                >
                  Изтегли легенда и обяснения в PDF формат
                </Button>
              </Stack>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              Специфични масиви по източници:
            </Typography>

            <Grid container spacing={2}>
              {downloadFiles.map((file) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={file.id}>
                  <Paper
                    sx={{
                      p: 2.5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                      <TableChart color="primary" />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                        >
                          {file.description}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                        >
                          {file.title}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, flexGrow: 1 }}
                    >
                      Изтеглете данните в предпочитания от вас формат:
                    </Typography>

                    <Stack spacing={1.5} sx={{ mt: "auto" }}>
                      <Button
                        startIcon={<Visibility />}
                        variant="contained"
                        fullWidth
                        onClick={() => navigate(file.viewPath)}
                        sx={{
                          justifyContent: "center",
                          backgroundColor: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.custom.secondaryOrangeGradient,
                          },
                        }}
                      >
                        Разгледай онлайн
                      </Button>

                      <Box display="flex" gap={1.5}>
                        <Button
                          startIcon={excelIcon}
                          variant="contained"
                          fullWidth
                          href={file.excelFile}
                          download={file.excelName}
                          sx={{
                            justifyContent: "center",
                            border: 1,
                            borderColor: "#1976d2",
                            backgroundColor: "transparent",
                            color: "#1976d2",
                            "&:hover": {
                              backgroundColor: "#e6f2ff",
                              borderColor: "#1565c0",
                            },
                          }}
                        >
                          Excel формат
                        </Button>

                        <Button
                          startIcon={<PictureAsPdf />}
                          variant="outlined"
                          fullWidth
                          href={file.pdfFile}
                          download={file.pdfName}
                          sx={{
                            justifyContent: "center",
                            borderColor: "#d32f2f",
                            color: "#d32f2f",
                            "&:hover": {
                              borderColor: "#c62828",
                              backgroundColor: "rgba(211, 47, 47, 0.04)",
                            },
                          }}
                        >
                          PDF формат
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e0e0e0" }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Статистика на базата данни
          </Typography>

          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip
              icon={<Description />}
              label="3597 читалища"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<Public />}
              label="265 общини"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<TableChart />}
              label="100+ индикатора"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<Assessment />}
              label="2021-2023 период"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<Search />}
              label="5 източника"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default DatabasePage;
